<template>
  <tr>
    <td class="b2b-grid__cell lg:table-cell">
      <grid-checkbox
        :qaid="`sku-grid-checkbox-${product.sku}`"
        :label="product.sku"
        :checked="isChecked"
        @change="handleCheck"
      />
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.icon"></td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.note">
      <grid-note
        :data="{
          value: 'note_add',
          editable: true,
          content: product.lineNote,
          hasNotification: product.lineNote != null && product.lineNote.length,
        }"
        :item-id="product.sku"
        :max-length="60"
        :base-properties="baseProperties"
        @note-added="handleAddNote"
      ></grid-note>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.sku">
      <div class="b2b-grid__label" v-html="bp('sku')"></div>
      <div class="b2b-grid__content">
        <div class="flex items-center">
          <a
            :href="`${encodedContextPath}/my-account/orders?partNumber=${product.sku}`"
            class="leading-none mr-1"
            qaid="sku-grid.orderHistory"
          >
            <i class="material-icons">history</i>
          </a>
          <grid-text
            class="flex"
            :data="{
              value: product.sku,
              tooltip: product.mfr ? {MFR: product.mfr} : null,
              link: encodedContextPath + '/p/' + product.sku,
            }"
          />
        </div>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.upc">
      <div class="b2b-grid__label" v-html="bp('upc')"></div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: product.ean ? product.ean : product.upc}"></grid-text>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell h-auto lg:text-right" qaid="sku-grid.allocated">
      <div class="b2b-grid__label" v-html="bp('allocated')"></div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: product.quantityAllocated == 0 ? null : product.quantityAllocated}"></grid-text>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell h-auto lg:text-right" qaid="sku-grid.backordered">
      <div class="b2b-grid__label" v-html="bp('backordered')"></div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: product.quantityBackordered == 0 ? null : product.quantityBackordered}"></grid-text>
      </div>
    </td>

    <!-- Variable product variants -->
    <template v-for="(variant, variantIndex) in variantList">
      <td :key="variantIndex" class="b2b-grid__cell lg:table-cell">
        <!-- Labels (small view only) -->
        <div class="b2b-grid__label">
          <span v-if="variant.name == 'Size'" v-html="bp('size')"></span>

          <span v-else-if="variant.name == 'Color'" v-html="bp('color')"></span>

          <span v-else>{{ variant.name }}</span>
        </div>

        <div class="b2b-grid__content">
          <grid-text :data="{value: variant.value}"></grid-text>
        </div>
      </td>
    </template>

    <td
      v-if="!consumerFriendlyMode"
      class="b2b-grid__cell lg:table-cell text-left lg:text-right"
      qaid="sku-grid.order-price"
    >
      <div class="b2b-grid__label" v-html="bp('price')"></div>
      <div class="b2b-grid__content">
        <grid-price
          :data="{
            dealerCost: product.price.dealerCost,
            discount: discount,
          }"
        />
      </div>
    </td>
    <td qaid="sku-grid.list" class="b2b-grid__cell lg:table-cell text-left lg:text-right">
      <div class="b2b-grid__label">
        <i class="md-18">list</i>
      </div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: product.skuQtyInSavedList > 0 ? product.skuQtyInSavedList : ''}"></grid-text>
      </div>
    </td>
    <td class="b2b-grid__cell lg:table-cell mb-4 md:mb-0 w-24" qaid="sku-grid.qty">
      <div class="b2b-grid__label" v-html="bp('qty')"></div>
      <div class="b2b-grid__content" :class="{'has-warning': minQtyError}">
        <numerical-input
          v-if="minQtyError"
          :key="product.sku"
          v-tippy="{
            html: `#qty-tooltip-${baseProductIndex}-${rowIndex}`,
            theme: 'light',
            maxWidth: '20rem',
            placement: 'top',
          }"
          :sku="product.sku"
          :initial-value="productQuantity"
          :data-sku="product.sku"
          :min="0"
          :max="9999"
          :data-initial-value="productQuantity"
          @blur.native="updateItemQuantity"
        />
        <numerical-input
          v-else
          :key="product.sku"
          :sku="product.sku"
          :initial-value="productQuantity"
          :data-sku="product.sku"
          :min="0"
          :max="9999"
          :data-initial-value="productQuantity"
          @blur.native="updateItemQuantity"
        />
      </div>
    </td>

    <div v-show="false" :id="`qty-tooltip-${baseProductIndex}-${rowIndex}`" v-tippy-html class="hidden">
      <span v-html="minQtyErrorMsg"></span>
    </div>

    <td v-if="!consumerFriendlyMode" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.sku">
      <div class="b2b-grid__label" v-html="bp('sku')"></div>
      <div class="b2b-grid__content lg:text-right">
        <grid-text :data="{value: product.lineTotal}"></grid-text>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.icon">
      <i :data-sku="product.sku" class="b2b-grid__icon material-icons cursor-pointer" @click="deleteItem">delete</i>
    </td>
  </tr>
</template>

<script>
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import {mapState, mapGetters, mapActions} from 'vuex';
import GridCheckbox from '@/components/grid/GridCheckbox';
import GridNote from '@/components/grid/GridNote';
import GridText from '@/components/grid/GridText';
import GridPrice from '@/components/grid/GridPrice';
import NumericalInput from '@/components/NumericalInput';

export default {
  components: {GridCheckbox, GridNote, GridText, GridPrice, NumericalInput},
  props: {
    baseProduct: {
      type: Object,
      default: () => {},
    },
    baseProductIndex: {
      type: Number,
      default: 0,
    },
    baseProperties: {
      type: Array,
      default: () => [],
    },
    product: {
      type: Object,
      default: () => {},
    },
    minQtyError: Boolean,
    minQtyErrorMsg: {
      type: String,
      default: '',
    },
    rowIndex: {
      type: Number,
      default: 0,
    },
    checkboxList: {
      type: Array,
      default: () => [],
    },
    addToCheckboxList: {
      type: Function,
      default: () => {},
    },
    removeFromCheckboxList: {
      type: Function,
      default: () => {},
    },
    updateCheckAll: {
      type: Function,
      default: () => {},
    },
  },

  computed: {
    isChecked() {
      if (this.checkboxList.some((product) => product === this.product.sku) === true) {
        return true;
      } else {
        return false;
      }
    },
    productQuantity() {
      return reduce(this.product.warehouses, (result, warehouse) => result + parseInt(warehouse.orderedQty), 0);
    },

    variantList() {
      const variantProduct = this.baseProduct;
      const categoriesObject = get(this.product, 'variantValueCategories[0]');

      if (categoriesObject) {
        const categories = Object.keys(categoriesObject);
        let variants = [];

        categories.forEach(function(category) {
          if (variantProduct.groupBy != category) {
            if (category == 'Color') {
              variants.push({name: category, value: categoriesObject[category].name});
            } else {
              variants.push({name: category, value: categoriesObject[category]});
            }
          }
        });

        return variants;
      }

      return null;
    },

    discount() {
      if (this.product && this.product.price) {
        if (this.advancedOrderingMode && this.product.price.advancedPrice) {
          return this.product.price.advancedPrice;
        } else if (this.product.price.sale && Number(this.product.price.sale.replace(/[^0-9.-]+/g, '')) > 0) {
          return this.product.price.sale;
        }
      }

      return 0;
    },
    ...mapGetters('backend', ['consumerFriendlyMode']),
    ...mapGetters('cart', ['selectedProducts']),
    ...mapState('advancedOrdering', ['advancedOrderingMode']),
    ...mapState('backend', ['encodedContextPath']),
  },

  methods: {
    ...mapActions('cart', ['addSelectedProduct', 'removeSelectedProduct', 'updateSelectedProduct']),
    handleCheck(event) {
      const payload = {
        sku: this.product.sku,
        qty: this.productQuantity,
        productType: this.product.productType,
        lineNote: this.product.lineNote,
      };
      if (event) {
        this.addToCheckboxList(this.product.sku);
        this.addSelectedProduct(payload);
      } else {
        this.removeFromCheckboxList(this.product.sku);
        this.removeSelectedProduct(this.product.sku);
      }
      this.updateCheckAll();
    },
    handleAddNote({id, content}) {
      const payload = {
        sku: id,
        lineNote: content,
      };
      this.$emit('send-cart-update', [payload]);
      this.updateSelectedProduct(payload);
    },

    deleteItem(event) {
      let itemSku = event.target.dataset.sku;

      if (!isEmpty(itemSku)) {
        this.updateCartQuantity(itemSku, 0, event.target);
      }
    },

    updateItemQuantity(event) {
      let itemSku = event.target.dataset.sku;
      let newQty = event.target.value;

      if (isEmpty(itemSku)) return;
      if (isEmpty(newQty)) newQty = 0;

      return this.updateCartQuantity(itemSku, newQty, event.target);
    },

    updateCartQuantity(itemSku, newQty, element) {
      if (newQty < 1) {
        this.$emit('open-confirm-delete-dialog', element);
      } else {
        // Update local warehouse qty first so that Vue updates correctly once the ajax cart updates finishes
        this.product.warehouses.forEach((warehouse, index) => {
          warehouse.orderedQty = index < 1 ? newQty : 0;
        });

        this.$emit('send-cart-update', [
          {
            sku: itemSku,
            qty: newQty,
          },
        ]);
        if (this.isChecked) {
          this.updateSelectedProduct({
            sku: itemSku,
            qty: newQty,
          });
        }
      }
    },
  },
};
</script>
